import { cn } from 'cn';
import { WordLayer } from './WordLayer';
import type { GlossedWord } from './learningApi';
import { SHOWS_ARABIC, SHOWS_TRANSLATION, type ViewMode } from './viewMode';
import type { PassageReading } from './types';

/**
 * One passage: the Arabic and its translation.
 *
 * Stacked on phones, side by side on desktop. The rules that are not
 * negotiable, from the design system's `PassageBlock` spec:
 *
 * - **Qur'an and hadith are quote variants** — framed, never bold, never
 *   coloured. The frame *is* the emphasis.
 * - **Qur'an is wrapped in ﴿ ﴾** and labelled *translation of meaning*,
 *   because a Qur'an translation is always a translation of meaning and never
 *   the verse itself.
 * - **An unconfirmed reference says so.** "detected by matching" stays until an
 *   editor confirms it.
 * - **Never strip tashkeel from a quotation.** Nothing here touches the text.
 * - The passage number sits in the start margin and is what citations point
 *   at. The design system draws it as a pilcrow; we do not — see the comment
 *   at the marker for why.
 */

const SECTION_LABELS: Record<string, string> = {
  opening: 'Opening',
  first_khutbah: 'First khutbah',
  second_khutbah: 'Second khutbah',
  supplication: 'Supplication',
  footnotes: 'Footnotes',
  other: 'Passage',
};

function QuranIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0"
    >
      <path d="M12 6.5c-1.6-1.4-3.9-2-7-2v13c3.1 0 5.4.6 7 2 1.6-1.4 3.9-2 7-2v-13c-3.1 0-5.4.6-7 2zM12 6.5v13" />
    </svg>
  );
}

function HadithIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0"
    >
      <path d="M6 15a3 3 0 0 1 0-6h2v-1a3 3 0 0 0-3-3M15 15a3 3 0 0 1 0-6h2v-1a3 3 0 0 0-3-3" />
    </svg>
  );
}

function MeaningIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0"
    >
      <path d="M4 6h9M8.5 4v2M11 6c-.6 3.5-2.8 6.5-6 8.5M6.2 9c1.2 2.4 3 4.2 5.3 5.5M13 20l3.5-9 3.5 9M14.3 17h4.4" />
    </svg>
  );
}

export function SectionLabel({
  sectionType,
  pageNo,
}: {
  sectionType: string;
  pageNo: number | null;
}) {
  const name = SECTION_LABELS[sectionType] ?? SECTION_LABELS.other;
  return (
    <p className="mt-10 mb-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {name}
      {/* A Word original has no pages, so the page half is simply absent
          rather than shown as "page null" or guessed at. */}
      {pageNo !== null ? ` · page ${pageNo}` : ''}
    </p>
  );
}

/** The source text, framed when it is a quotation. */
function SourceText({
  passage,
  sourceLang,
  sourceDir,
  sourceScript,
}: {
  passage: PassageReading;
  sourceLang: string;
  sourceDir: 'ltr' | 'rtl';
  sourceScript: string;
}) {
  const isQuran = passage.passageType === 'quran';
  const isHadith = passage.passageType === 'hadith';
  const isQuote = isQuran || isHadith;

  if (!passage.sourceText) {
    // A Qur'an passage legitimately has no text yet: QCF glyph indices are
    // never persisted as text, so until the glyph map decodes it there is
    // nothing truthful to show (ADR 0007). Saying so is better than an empty
    // frame that reads as a rendering failure.
    return (
      <p className="rounded-md border border-dashed border-quote-arabic-border bg-quote-arabic-bg px-4 py-3 text-sm text-muted-foreground">
        {isQuran
          ? 'This verse is quoted in a Qur’an font whose glyphs are not yet decoded, so it is not shown rather than shown wrongly.'
          : 'The Arabic for this passage could not be read from the original.'}
      </p>
    );
  }

  const text = (
    <p
      lang={sourceLang}
      dir={sourceDir}
      className={cn(
        sourceScript,
        'text-reading-foreground',
        // Never bold, never coloured, for a quotation or otherwise.
        'font-normal',
      )}
    >
      {isQuran ? (
        <>
          <span className="text-citation-foreground" aria-hidden>
            ﴿
          </span>{' '}
          {passage.sourceText}{' '}
          <span className="text-citation-foreground" aria-hidden>
            ﴾
          </span>
        </>
      ) : (
        passage.sourceText
      )}
    </p>
  );

  if (!isQuote) {
    return text;
  }

  return (
    <div className="rounded-md border border-quote-arabic-border bg-quote-arabic-bg p-4">
      <div className="mb-3 flex flex-col gap-1.5">
        <span className="inline-flex items-center gap-1.5 self-start rounded-sm bg-citation-bg px-2 py-1 text-[11px] font-semibold text-citation-foreground">
          {isQuran ? <QuranIcon /> : <HadithIcon />}
          <span>{isQuran ? 'Qur’an' : 'Hadith'}</span>
          {/* Mandatory until an editor confirms the reference. The reference
              itself is absent because nothing has matched it yet — claiming a
              surah and ayah we have not verified is the one thing the project
              forbids outright. */}
          <span className="font-normal italic opacity-80">
            · reference not yet confirmed
          </span>
        </span>
        {isQuran ? (
          <span className="inline-flex items-center gap-1.5 self-start text-[11px] text-muted-foreground">
            <MeaningIcon />
            <span>Translation of meaning</span>
          </span>
        ) : null}
      </div>
      {text}
    </div>
  );
}

function TranslationText({
  passage,
  scriptClass,
  direction,
  languageCode,
}: {
  passage: PassageReading;
  scriptClass: string;
  direction: 'ltr' | 'rtl';
  languageCode: string;
}) {
  if (!passage.translation) {
    if (passage.passageType === 'quran') {
      // Deliberate, not missing. Said plainly so the gap does not read as a
      // failure to translate.
      return (
        <p dir={direction} className="text-sm italic text-muted-foreground">
          Qur’an is not translated here — it is matched against the canonical
          text, and its meaning is shown once the reference is confirmed.
        </p>
      );
    }
    return (
      <p dir={direction} className="text-sm italic text-muted-foreground">
        Not translated yet.
      </p>
    );
  }

  return (
    // Direction is set on the PANE, not only on the paragraph. With it on the
    // <p> alone, the metadata row and the badge below stayed left-aligned
    // inside a right-to-left pane -- visible immediately in Urdu, where the
    // text runs one way and its own footnote the other.
    <div className="flex flex-col gap-2" dir={direction} lang={languageCode}>
      <p className={cn(scriptClass, 'text-reading-foreground')}>
        {passage.translation.text}
      </p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
        {/* The margin already carries the passage number, so repeating it here
            was twice the clutter for none of the information. Only the page
            remains, and only when there is one -- a Word original has none. */}
        {passage.pageNo !== null ? <span>Page {passage.pageNo}</span> : null}
        {/* "not yet checked for faithfulness" was here on every passage.
            It is one fact about the whole translation, it is jargon, and it
            now lives in the trust strip's popover in words a reader
            understands. */}
      </div>
    </div>
  );
}

export function PassageBlock({
  passage,
  scriptClass,
  direction,
  languageCode,
  sourceLang,
  sourceDir,
  sourceScript,
  words,
  onReport,
  reportStatus,
  view,
}: {
  passage: PassageReading;
  scriptClass: string;
  direction: 'ltr' | 'rtl';
  languageCode: string;
  /** The khutbah's own language, from the server. Never assumed here. */
  sourceLang: string;
  sourceDir: 'ltr' | 'rtl';
  sourceScript: string;
  /** Empty unless the reader turned word meaning on and glosses exist. */
  words: readonly GlossedWord[];
  /** Opens the report form for this passage. */
  onReport?: (() => void) | undefined;
  /** What this device was told about its own report, if it filed one. */
  reportStatus?: string | undefined;
  view: ViewMode;
}) {
  const sideBySide = view === 'side-by-side';
  const showArabic = SHOWS_ARABIC[view];
  // A Qur'an passage has no translation by design (ADR 0007), so in
  // translation-only view it would vanish -- and a reader would have no way to
  // know a verse was recited at all. Its Arabic is shown in every mode for
  // that reason: omitting it would misrepresent the khutbah, which is a
  // different and worse failure than showing untranslated text.
  const showArabicAnyway = passage.passageType === 'quran';
  const showTranslation = SHOWS_TRANSLATION[view] && passage.translation !== null;

  // Shown in EVERY mode, including translation-only.
  //
  // `sourceUncertain` means text was removed from this passage because it could
  // not be read -- glyph codes with no font attribution, most often a Qur'an
  // quotation. A reader seeing the shortened prose has no way to know a piece
  // is missing, and in translation-only view the Arabic pane that might have
  // hinted at it is not even on screen. So the notice hangs off the passage
  // rather than off either pane.
  //
  // The Arabic pane already explains a passage whose source is entirely
  // absent, so the notice stands down in that one case to avoid saying it
  // twice -- but only when that pane is actually being rendered. Translation-
  // only view was showing six warnings where every other mode showed seven,
  // because the empty-source message lives in a pane that view does not draw.
  //
  // The notice deliberately says only that text is missing, not what kind.
  // It used to add "A quotation is most likely missing", which the flag does
  // not establish: `sourceUncertain` means characters could not be read, and
  // those could as easily have been a mis-encoded ligature as a verse. Review
  // caught the overstatement, and it is the same habit this whole area kept
  // falling into -- claiming more than the evidence carries.
  const arabicPaneExplains =
    (showArabic || showArabicAnyway) && passage.sourceText.length === 0;
  const incomplete = passage.sourceUncertain && !arabicPaneExplains;

  return (
    <article
      // The anchor a citation will scroll to in Phase 4.
      id={`passage-${passage.passageId}`}
      className={cn(
        'group/passage relative scroll-mt-24 py-5',
        sideBySide ? 'grid gap-8 md:grid-cols-2 md:items-start' : 'flex flex-col gap-4',
      )}
    >
      {incomplete ? (
        <p
          className={cn(
            'rounded-md border border-dashed border-warning bg-warning-bg',
            'px-3 py-2 text-sm text-warning',
            sideBySide && 'md:col-span-2',
          )}
        >
          Part of this passage could not be read from the original and is not
          shown.
        </p>
      ) : null}

      {/*
        A plain number, not a pilcrow.

        The design system specifies "¶ 4" here, and the glyph is a
        proofreading mark whose only purpose is to be a citation target --
        which is Phase 4 and does not exist yet. So today it put an
        unexplained symbol in front of every reader for no function they
        could use. The number stays, because it IS the anchor and reads like
        a verse number in a printed book; the mark goes.

        Logical property, not `left`: in an RTL interface this margin moves to
        the other side, and `left-` would be a bug.
      */}
      {/* Reporting lives on the passage, because that is what a reader is
          looking at when they notice something — not in a menu elsewhere. */}
      {reportStatus ? (
        <p className="absolute -top-1 end-0 text-[11px] italic text-muted-foreground">
          {reportStatus}
        </p>
      ) : onReport ? (
        <button
          type="button"
          onClick={onReport}
          className="absolute -top-1 end-0 text-[11px] text-muted-foreground underline opacity-0 transition-opacity focus-visible:opacity-100 group-hover/passage:opacity-100"
        >
          Report a problem
        </button>
      ) : null}

      <span
        className="absolute -top-1 start-0 -ms-8 hidden text-[11px] text-muted-foreground md:block"
        aria-hidden
      >
        {/* One-based, matching the editor. `ordinal` is the storage order
            and starts at zero; the anchor above is the passage id, so the
            number shown is free to be the one a person would say. */}
        {passage.ordinal + 1}
      </span>

      {showArabic || showArabicAnyway ? (
        <div className="flex flex-col">
          <SourceText
            passage={passage}
            sourceLang={sourceLang}
            sourceDir={sourceDir}
            sourceScript={sourceScript}
          />
          <WordLayer
            words={words}
            lang={sourceLang}
            dir={sourceDir}
            scriptClass={sourceScript}
            translationScript={scriptClass}
            translationDir={direction}
          />
        </div>
      ) : null}

      {/*
        Translation-only, with word meaning on: the Arabic is not otherwise
        on screen, so the chips bring it in beneath the passage. The kit's
        answer to "what does word meaning do here", and better than
        disabling the control in a mode where it still has something to say.
      */}
      {!showArabic && !showArabicAnyway && words.length > 0 ? (
        <WordLayer
          words={words}
          lang={sourceLang}
          dir={sourceDir}
          scriptClass={sourceScript}
          translationScript={scriptClass}
          translationDir={direction}
        />
      ) : null}

      {SHOWS_TRANSLATION[view] ? (
        <div
          className={cn(
            // The stacked variant always gives the translation its own ground,
            // because stacked is what a phone IS -- the two-column grid
            // collapses below md whatever the view says.
            showTranslation ? 'rounded-lg bg-translation-bg p-4' : undefined,
            // At md the side-by-side variant drops the panel for a hairline.
            sideBySide && showTranslation
              ? 'md:rounded-none md:bg-transparent md:p-0 md:border-s md:border-border md:ps-8'
              : undefined,
          )}
        >
          <TranslationText
            passage={passage}
            scriptClass={scriptClass}
            direction={direction}
            languageCode={languageCode}
          />
          {/* No provenance badge here.
              Both trust claims belong to the TRANSLATION, per language —
              not to a passage. Repeating them produced twelve labels on a
              six-passage khutbah and said nothing the header had not already
              said once. ReaderSurface.d.ts states it outright: "PassageBlock
              no longer carries them." */}
        </div>
      ) : null}
    </article>
  );
}
